/**
 * Table-play edits: level, floor, notes — the fields adjusted mid-campaign.
 *
 * These route through the SAME pipeline the desktop app uses:
 *   draftFromCharacter -> edit draft -> changesFromDraft -> applyChanges
 * so every edit is a rules-checked CharacterChange, not a raw field write. For
 * these three fields no skill/item catalogue is needed (they don't touch
 * skills or inventory), so applyChanges runs without one.
 *
 * Deliberately scoped to "A" edits. Rewriting the five core stats or skills is
 * a separate, higher-risk slice (a stat change cascades into health/mana), left
 * for later.
 */
import type { Character } from '@dcc/rules/domain/character.ts';
import { draftFromCharacter, changesFromDraft } from '@dcc/rules/app/characterEditService.ts';
import { applyChanges } from '@dcc/rules/rules/characterChanges.ts';

export interface TablePlayEdits {
  level?: number;
  floor?: number;
  notes?: string;
}

export type EditResult =
  | { ok: true; character: Character }
  | { ok: false; problems: string[] };

/**
 * Apply level/floor/notes edits to a character through the rules engine.
 * Only the provided fields change; the rest of the draft mirrors the current
 * character, so changesFromDraft emits a minimal, checked batch.
 */
export function applyTablePlayEdits(character: Character, edits: TablePlayEdits): EditResult {
  const draft = draftFromCharacter(character);
  if (edits.level !== undefined) draft.level = edits.level;
  if (edits.floor !== undefined) draft.floor = edits.floor;
  if (edits.notes !== undefined) draft.notes = edits.notes;

  const changes = changesFromDraft(character, draft);
  const requests = changes.map((change) => ({ change, source: 'manual-edit' as const, reason: 'table edit' }));

  const result = applyChanges(character, requests, { now: new Date().toISOString() });
  if (!result.ok) return { ok: false, problems: result.problems.map((p) => p.message) };
  return { ok: true, character: result.character };
}
